import { motion } from 'framer-motion';
import { Wine, Waves, Sun, Users, MapPin, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const VibrantPenticton = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const itineraries = [
    {
      title: '1-Day Penticton Perfect Day',
      description: 'A whirlwind tour of the best Penticton has to offer',
      activities: [
        'Morning beach walk at Okanagan Lake',
        'Brunch at a local vineyard',
        'Afternoon float down the canal',
        'Dinner with a lake view',
        'Sunset at Munson Mountain'
      ]
    },
    {
      title: '3-Day Okanagan Explorer',
      description: 'Wine, water, and family adventures',
      activities: [
        'Day 1: Beach life and water sports',
        'Day 2: Wine tour with kid-friendly activities',
        'Day 3: Hiking trails and local markets'
      ]
    },
    {
      title: '7-Day Penticton Immersion',
      description: 'Live like a local for a full week',
      activities: [
        'Multiple vineyard visits',
        'Explore nearby Naramata and Summerland',
        'Try local restaurants and cafes',
        'Family activities and outdoor adventures',
        'Community events and farmers markets'
      ]
    }
  ];

  const attractions = [
    {
      icon: <Waves className="w-8 h-8" />,
      title: 'Beaches & Water',
      description: 'Two pristine beaches, the famous channel float, and endless water activities',
      color: '#1E6FBA'
    },
    {
      icon: <Wine className="w-8 h-8" />,
      title: 'Wineries',
      description: 'World-class vineyards with stunning views and family-friendly experiences',
      color: '#2E8B57'
    },
    {
      icon: <Sun className="w-8 h-8" />,
      title: '300+ Days of Sunshine',
      description: 'Year-round outdoor adventures in one of Canada\'s sunniest cities',
      color: '#F28A30'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Family Activities',
      description: 'Playgrounds, museums, trails, and kid-approved adventures everywhere',
      color: '#1E6FBA'
    }
  ];

  return (
    <div className="w-full" data-testid="vibrant-penticton-page">
      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_fifa-fan-guides/artifacts/m5tn6mvo_Evan%20Wishloff%20-%20Okanagan%20Trestle%20Tour%20-%202023-2999.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mb-6" data-testid="page-title">
              Vibrant Penticton
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Where lake life meets wine country, and every day feels like a vacation
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Penticton */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="why-penticton-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl sm:text-5xl text-center mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Why Penticton?
          </h2>
          <p className="text-center text-[#4A4A4A] text-lg mb-16 max-w-3xl mx-auto leading-relaxed">
            Nestled between two sparkling lakes in the heart of British Columbia's wine country, Penticton is where adventure meets relaxation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {attractions.map((attraction, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-sm card-hover"
              data-testid={`attraction-${index}`}
            >
              <div style={{ color: attraction.color }} className="mb-4">
                {attraction.icon}
              </div>
              <h3 className="text-2xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                {attraction.title}
              </h3>
              <p className="text-[#4A4A4A] leading-relaxed">{attraction.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Itineraries */}
      <section className="bg-[#F5E9D4] grain-texture py-20" data-testid="itineraries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Plan Your Perfect Visit
            </h2>
            <p className="text-[#4A4A4A] text-lg">
              Choose your adventure length and let us guide you
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {itineraries.map((itinerary, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm"
                data-testid={`itinerary-${index}`}
              >
                <Calendar className="w-10 h-10 text-[#1E6FBA] mb-4" />
                <h3 className="text-2xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {itinerary.title}
                </h3>
                <p className="text-[#4A4A4A] mb-6">{itinerary.description}</p>
                <ul className="space-y-2">
                  {itinerary.activities.map((activity, actIndex) => (
                    <li key={actIndex} className="flex items-start">
                      <ArrowRight className="w-4 h-4 text-[#2E8B57] mr-2 mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4A4A4A]">{activity}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Walking Tours CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="walking-tours-cta">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white p-12 rounded-3xl shadow-sm text-center"
        >
          <h2 className="text-4xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Join Our Summer Walking Tours
          </h2>
          <p className="text-[#4A4A4A] text-lg mb-8 max-w-2xl mx-auto">
            Experience Penticton's stories, culture, and community on foot. Perfect for visitors and locals alike!
          </p>
          <Link to="/walking-tours">
            <Button
              size="lg"
              className="btn-scale bg-[#1E6FBA] hover:bg-[#1558A0] text-white rounded-full px-8 py-6 text-lg"
              data-testid="cta-walking-tours"
            >
              Explore Walking Tours
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="bg-[#1E6FBA] py-16" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Let's Plan Your Penticton Experience
            </h2>
            <Link to="/contact">
              <Button
                size="lg"
                className="btn-scale bg-white text-[#1E6FBA] hover:bg-gray-100 rounded-full px-8 py-6"
                data-testid="cta-contact"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VibrantPenticton;
